import { createBrowserRouter, Navigate } from "react-router-dom";
import { ProtectedShell } from "./ProtectedShell";
import { RoleRedirect } from "./RoleRedirect";
import { RootError } from "./RootError";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RoleRedirect />,
    errorElement: <RootError />,
  },
  {
    path: "/login",
    lazy: async () => {
      const m = await import("../routes/login");
      return { Component: m.LoginPage };
    },
  },
  {
    element: <ProtectedShell role="owner" />,
    errorElement: <RootError />,
    children: [
      {
        path: "/owner/dashboard",
        lazy: async () => {
          const m = await import("../routes/owner/dashboard");
          return { Component: m.OwnerDashboardPage };
        },
      },
      {
        path: "/owner/schedule",
        lazy: async () => {
          const m = await import("../routes/owner/schedule");
          return { Component: m.OwnerSchedulePage };
        },
      },
      {
        path: "/owner/coaches",
        lazy: async () => {
          const m = await import("../routes/owner/coaches");
          return { Component: m.OwnerCoachesPage };
        },
      },
      {
        path: "/owner/coaches/:id",
        lazy: async () => {
          const m = await import("../routes/owner/coach-detail");
          return { Component: m.OwnerCoachDetailPage };
        },
      },
      {
        path: "/owner/coaches/:id/disputes",
        lazy: async () => {
          const m = await import("../routes/owner/coach-disputes");
          return { Component: m.OwnerCoachDisputesPage };
        },
      },
      {
        // M5 (T039) — 老闆教練離職批量處理頁；唯一新 route
        path: "/owner/coaches/:id/offboarding",
        lazy: async () => {
          const m = await import("../routes/owner/coach-offboarding");
          return { Component: m.OwnerCoachOffboardingPage };
        },
      },
      {
        path: "/owner/settings/branches",
        lazy: async () => {
          const m = await import("../routes/owner/settings-branches");
          return { Component: m.OwnerSettingsBranchesPage };
        },
      },
      {
        path: "/owner/students",
        lazy: async () => {
          const m = await import("../routes/owner/students");
          return { Component: m.OwnerStudentsPage };
        },
      },
      {
        path: "/owner/students/:id",
        lazy: async () => {
          const m = await import("../routes/owner/student-detail");
          return { Component: m.OwnerStudentDetailPage };
        },
      },
      {
        path: "/owner/leads",
        lazy: async () => {
          const m = await import("../routes/owner/leads");
          return { Component: m.OwnerLeadsPage };
        },
      },
      {
        path: "/owner/leads/sleeping",
        lazy: async () => {
          const m = await import("../routes/owner/leads-sleeping");
          return { Component: m.OwnerSleepingLeadsPage };
        },
      },
      {
        path: "/owner/leads/:id",
        lazy: async () => {
          const m = await import("../routes/owner/lead-detail");
          return { Component: m.OwnerLeadDetailPage };
        },
      },
      {
        path: "/owner/reports",
        lazy: async () => {
          const m = await import("../routes/owner/reports");
          return { Component: m.OwnerReportsPage };
        },
      },
      {
        path: "/owner/reports/monthly",
        lazy: async () => {
          const m = await import("../routes/owner/reports-monthly");
          return { Component: m.OwnerMonthlyReportPage };
        },
      },
      {
        path: "/owner/reports/coach-ranking",
        lazy: async () => {
          const m = await import("../routes/owner/reports-coach-ranking");
          return { Component: m.OwnerCoachRankingReportPage };
        },
      },
      {
        path: "/owner/reports/facility-utilization",
        lazy: async () => {
          const m = await import("../routes/owner/reports-facility-utilization");
          return { Component: m.OwnerFacilityUtilReportPage };
        },
      },
      {
        path: "/owner/reports/student-flow",
        lazy: async () => {
          const m = await import("../routes/owner/reports-student-flow");
          return { Component: m.OwnerStudentFlowReportPage };
        },
      },
      {
        path: "/owner/settlements",
        lazy: async () => {
          const m = await import("../routes/owner/settlements");
          return { Component: m.OwnerSettlementsPage };
        },
      },
      {
        path: "/owner/settlements/:id",
        lazy: async () => {
          const m = await import("../routes/owner/settlement-detail");
          return { Component: m.OwnerSettlementDetailPage };
        },
      },
      {
        path: "/owner/reports/coach-disputes",
        lazy: async () => {
          const m = await import("../routes/owner/reports-coach-disputes");
          return { Component: m.OwnerCoachDisputesReportPage };
        },
      },
      {
        path: "/owner/reports/recruitment-funnel",
        lazy: async () => {
          const m = await import("../routes/owner/reports-recruitment-funnel");
          return { Component: m.OwnerRecruitmentFunnelPage };
        },
      },
      {
        path: "/owner/reports/referrers",
        lazy: async () => {
          const m = await import("../routes/owner/reports-referrers");
          return { Component: m.OwnerReferrersPage };
        },
      },
      {
        path: "/owner/incidents",
        lazy: async () => {
          const m = await import("../routes/owner/incidents");
          return { Component: m.OwnerIncidentsPage };
        },
      },
      {
        // Dev-only preview page; route registered before the dynamic
        // `/owner/incidents/:id` so the literal `_cross-module-preview`
        // segment matches first. Production builds keep the route but the
        // page is clearly marked [Dev] and is not linked from sidebar nav.
        path: "/owner/incidents/_cross-module-preview",
        lazy: async () => {
          const m = await import("../routes/owner/cross-module-preview");
          return { Component: m.CrossModulePreviewPage };
        },
      },
      {
        // 待審 drafts (US5) — literal segment, registered before :id catch-all
        path: "/owner/incidents/drafts",
        lazy: async () => {
          const m = await import("../routes/owner/incident-drafts");
          return { Component: m.OwnerIncidentDraftsPage };
        },
      },
      {
        path: "/owner/incidents/:id",
        lazy: async () => {
          const m = await import("../routes/owner/incident-detail");
          return { Component: m.OwnerIncidentDetailPage };
        },
      },
    ],
  },
  {
    element: <ProtectedShell role="manager" />,
    errorElement: <RootError />,
    children: [
      {
        path: "/manager/dashboard",
        lazy: async () => {
          const m = await import("../routes/manager/dashboard");
          return { Component: m.ManagerDashboardPage };
        },
      },
      {
        path: "/manager/schedule",
        lazy: async () => {
          const m = await import("../routes/manager/schedule");
          return { Component: m.ManagerSchedulePage };
        },
      },
      {
        path: "/manager/coaches",
        lazy: async () => {
          const m = await import("../routes/manager/coaches");
          return { Component: m.ManagerCoachesPage };
        },
      },
      {
        path: "/manager/coaches/:id",
        lazy: async () => {
          const m = await import("../routes/manager/coach-detail");
          return { Component: m.ManagerCoachDetailPage };
        },
      },
      {
        path: "/manager/coaches/:id/disputes",
        lazy: async () => {
          const m = await import("../routes/manager/coach-disputes");
          return { Component: m.ManagerCoachDisputesPage };
        },
      },
      {
        path: "/manager/students",
        lazy: async () => {
          const m = await import("../routes/manager/students");
          return { Component: m.ManagerStudentsPage };
        },
      },
      {
        path: "/manager/leads",
        lazy: async () => {
          const m = await import("../routes/manager/leads");
          return { Component: m.ManagerLeadsPage };
        },
      },
      {
        path: "/manager/leads/sleeping",
        lazy: async () => {
          const m = await import("../routes/manager/leads-sleeping");
          return { Component: m.ManagerSleepingLeadsPage };
        },
      },
      {
        path: "/manager/leads/:id",
        lazy: async () => {
          const m = await import("../routes/manager/lead-detail");
          return { Component: m.ManagerLeadDetailPage };
        },
      },
      {
        path: "/manager/reports",
        lazy: async () => {
          const m = await import("../routes/manager/reports");
          return { Component: m.ManagerReportsPage };
        },
      },
      {
        path: "/manager/reports/monthly",
        lazy: async () => {
          const m = await import("../routes/manager/reports-monthly");
          return { Component: m.ManagerMonthlyReportPage };
        },
      },
      {
        path: "/manager/reports/coach-ranking",
        lazy: async () => {
          const m = await import("../routes/manager/reports-coach-ranking");
          return { Component: m.ManagerCoachRankingReportPage };
        },
      },
      {
        path: "/manager/reports/facility-utilization",
        lazy: async () => {
          const m = await import("../routes/manager/reports-facility-utilization");
          return { Component: m.ManagerFacilityUtilReportPage };
        },
      },
      {
        path: "/manager/reports/student-flow",
        lazy: async () => {
          const m = await import("../routes/manager/reports-student-flow");
          return { Component: m.ManagerStudentFlowReportPage };
        },
      },
      {
        path: "/manager/reports/coach-disputes",
        lazy: async () => {
          const m = await import("../routes/manager/reports-coach-disputes");
          return { Component: m.ManagerCoachDisputesReportPage };
        },
      },
      {
        path: "/manager/reports/recruitment-funnel",
        lazy: async () => {
          const m = await import("../routes/manager/reports-recruitment-funnel");
          return { Component: m.ManagerRecruitmentFunnelPage };
        },
      },
      {
        path: "/manager/incidents",
        lazy: async () => {
          const m = await import("../routes/manager/incidents");
          return { Component: m.ManagerIncidentsPage };
        },
      },
      {
        // 待審 drafts (US5) — literal segment before :id
        path: "/manager/incidents/drafts",
        lazy: async () => {
          const m = await import("../routes/manager/incident-drafts");
          return { Component: m.ManagerIncidentDraftsPage };
        },
      },
      {
        path: "/manager/incidents/:id",
        lazy: async () => {
          const m = await import("../routes/manager/incident-detail");
          return { Component: m.ManagerIncidentDetailPage };
        },
      },
    ],
  },
  // M2 (feature/3) — trainer route group; first page = my-referrals
  {
    element: <ProtectedShell role="trainer" />,
    errorElement: <RootError />,
    children: [
      {
        path: "/trainer/my-referrals",
        lazy: async () => {
          const m = await import("../routes/trainer/my-referrals");
          return { Component: m.TrainerMyReferralsPage };
        },
      },
      {
        path: "/trainer/students",
        lazy: async () => {
          const m = await import("../routes/trainer/students");
          return { Component: m.TrainerStudentsPage };
        },
      },
      {
        path: "/trainer/students/:id",
        lazy: async () => {
          const m = await import("../routes/trainer/student-detail");
          return { Component: m.TrainerStudentDetailPage };
        },
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
